import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Settings2, Database, Link as LinkIcon, ChevronDown, ChevronUp } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";

type CRMSettings = Tables<"crm_settings">;

export const CRMSettings = () => {
  const [settings, setSettings] = useState<CRMSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const { data, error } = await supabase
      .from('crm_settings')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching CRM settings:', error);
    } else if (data) {
      setSettings(data);
    } else {
      // Create default settings if none exist
      const { data: newSettings } = await supabase
        .from('crm_settings')
        .insert({ crm_type: 'built-in', is_enabled: true })
        .select()
        .single();
      setSettings(newSettings);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    const { error } = await supabase
      .from('crm_settings')
      .update({
        crm_type: settings.crm_type,
        is_enabled: settings.is_enabled,
        api_key: settings.api_key,
        api_url: settings.api_url
      })
      .eq('id', settings.id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to save CRM settings",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "CRM settings saved successfully",
      });
    }
    setSaving(false);
  };

  if (loading) {
    return <div>Loading CRM settings...</div>;
  }

  if (!settings) {
    return <div>No settings found</div>;
  }

  return (
    <Card>
      <CardHeader>
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="flex items-center justify-between w-full hover:opacity-80 transition-opacity">
            <div className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              <div className="text-left">
                <CardTitle>CRM Configuration</CardTitle>
                <CardDescription>
                  {isOpen 
                    ? "Choose between built-in CRM or connect to external platforms"
                    : `${settings.crm_type === 'built-in' ? 'Built-in CRM' : settings.crm_type} - ${settings.is_enabled ? 'Enabled' : 'Disabled'}`
                  }
                </CardDescription>
              </div>
            </div>
            {isOpen ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="space-y-6 pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable CRM</Label>
                  <p className="text-sm text-muted-foreground">
                    Activate customer relationship management
                  </p>
                </div>
                <Switch
                  checked={settings.is_enabled}
                  onCheckedChange={(checked) =>
                    setSettings({ ...settings, is_enabled: checked })
                  }
                />
              </div>

              {settings.is_enabled && (
                <>
                  <div className="space-y-2">
                    <Label>CRM Type</Label>
                    <Select
                      value={settings.crm_type}
                      onValueChange={(value) =>
                        setSettings({ ...settings, crm_type: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="built-in">
                          <div className="flex items-center gap-2">
                            <Database className="h-4 w-4" />
                            Built-in CRM (Recommended)
                          </div>
                        </SelectItem>
                        <SelectItem value="hubspot">
                          <div className="flex items-center gap-2">
                            <LinkIcon className="h-4 w-4" />
                            HubSpot
                          </div>
                        </SelectItem>
                        <SelectItem value="salesforce">
                          <div className="flex items-center gap-2">
                            <LinkIcon className="h-4 w-4" />
                            Salesforce (Coming Soon)
                          </div>
                        </SelectItem>
                        <SelectItem value="pipedrive">
                          <div className="flex items-center gap-2">
                            <LinkIcon className="h-4 w-4" />
                            Pipedrive (Coming Soon)
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {settings.crm_type !== 'built-in' && (
                    <>
                      <div className="space-y-2">
                        <Label>API Key</Label>
                        <Input
                          type="password"
                          placeholder="Enter your API key"
                          value={settings.api_key || ''}
                          onChange={(e) =>
                            setSettings({ ...settings, api_key: e.target.value })
                          }
                        />
                        <p className="text-xs text-muted-foreground">
                          Your API key is stored securely and never exposed
                        </p>
                      </div>

                      {settings.crm_type === 'hubspot' && (
                        <div className="rounded-lg border p-4 space-y-2">
                          <h4 className="font-medium text-sm">HubSpot Setup:</h4>
                          <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                            <li>Go to HubSpot Settings → Integrations → Private Apps</li>
                            <li>Create a new private app</li>
                            <li>Grant "CRM" scopes: contacts (read/write)</li>
                            <li>Copy the access token and paste above</li>
                          </ol>
                        </div>
                      )}
                    </>
                  )}

                  {settings.crm_type === 'built-in' && (
                    <div className="rounded-lg border p-4">
                      <div className="flex items-start gap-3">
                        <Database className="h-5 w-5 text-primary mt-0.5" />
                        <div className="space-y-1">
                          <h4 className="font-medium text-sm">Built-in CRM Features:</h4>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            <li>✓ Customer profiles with custom fields</li>
                            <li>✓ Lifecycle stages (Lead → Prospect → Customer)</li>
                            <li>✓ Tags and priority management</li>
                            <li>✓ Activity timeline tracking</li>
                            <li>✓ No external dependencies or costs</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <Button 
                onClick={handleSave} 
                disabled={saving} 
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </CardHeader>
    </Card>
  );
};
